import { type RestaurantBranding } from "api-types";
import {
  Timestamp,
  Unsubscribe,
  deleteDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  where,
} from "firebase/firestore";

import { FirestoreRefs } from "igfd-commons/src/firebase/refs";

import type { FirestoreThemeDocument, Theme } from "@dashboard/types/theme";

const readTheme = (doc: FirestoreThemeDocument): Theme => {
  const { createdAt, updatedAt, ...rest } = doc;
  return {
    ...rest,
    createdAtMillis: createdAt.toMillis(),
    updatedAtMillis: updatedAt.toMillis(),
  };
};

const writeTheme = (theme: Theme): FirestoreThemeDocument => {
  const { createdAtMillis, updatedAtMillis, ...rest } = theme;
  return {
    ...rest,
    createdAt: Timestamp.fromMillis(createdAtMillis),
    updatedAt: Timestamp.fromMillis(updatedAtMillis),
  };
};

export interface ThemeApi {
  subscribeToThemes(
    restaurantUuid: string,
    onChange: (themes: Theme[]) => void,
    onError: (error: Error) => void
  ): Unsubscribe;
  createTheme(
    restaurantUuid: string,
    id: string,
    name: string,
    branding: RestaurantBranding
  ): Promise<void>;
  saveTheme(restaurantUuid: string, theme: Theme): Promise<void>;
  renameTheme(
    restaurantUuid: string,
    themeId: string,
    name: string
  ): Promise<void>;
  deleteTheme(restaurantUuid: string, themeId: string): Promise<void>;
}

/**
 * An implementation of the ThemeApi that uses Firestore as the backend.
 * Uses the restaurant subcollection: restaurants-v1/{restaurantUuid}/themes/{themeId}
 */
export class FirestoreBackedThemeApi implements ThemeApi {
  private firestoreRefs: FirestoreRefs;

  constructor(params: { firestoreRefs: FirestoreRefs }) {
    this.firestoreRefs = params.firestoreRefs;
  }

  public subscribeToThemes(
    restaurantUuid: string,
    onChange: (themes: Theme[]) => void,
    onError: (error: Error) => void
  ): Unsubscribe {
    return onSnapshot(
      this.firestoreRefs.themes(restaurantUuid),
      (snapshot) =>
        onChange(
          snapshot.docs
            .map((doc) => doc.data() as FirestoreThemeDocument)
            .map(readTheme)
        ),
      onError
    );
  }

  public async createTheme(
    restaurantUuid: string,
    id: string,
    name: string,
    branding: RestaurantBranding
  ): Promise<void> {
    const nowMillis = Date.now();

    const theme: Theme = {
      ...branding,
      id,
      name,
      restaurantUuid,
      createdAtMillis: nowMillis,
      updatedAtMillis: nowMillis,
    };

    await setDoc(
      this.firestoreRefs.theme(restaurantUuid, id),
      writeTheme(theme)
    );
  }

  public async saveTheme(restaurantUuid: string, theme: Theme): Promise<void> {
    // Merged so a tab built before a field existed can't wipe it; clearing a
    // field means writing null.
    await setDoc(
      this.firestoreRefs.theme(restaurantUuid, theme.id),
      writeTheme(theme),
      { merge: true }
    );
  }

  public async renameTheme(
    restaurantUuid: string,
    themeId: string,
    name: string
  ): Promise<void> {
    await setDoc(
      this.firestoreRefs.theme(restaurantUuid, themeId),
      { name, updatedAt: Timestamp.now() },
      { merge: true }
    );
  }

  public async deleteTheme(
    restaurantUuid: string,
    themeId: string
  ): Promise<void> {
    // Check if any menu URL references this theme
    const menuUrlsSnapshot = await getDocs(
      query(
        this.firestoreRefs.menuUrlsV2(restaurantUuid),
        where("themeId", "==", themeId)
      )
    );
    if (!menuUrlsSnapshot.empty) {
      throw new ThemeInUseError(menuUrlsSnapshot.size);
    }

    await deleteDoc(this.firestoreRefs.theme(restaurantUuid, themeId));
  }
}

export class ThemeInUseError extends Error {
  public readonly menuUrlCount: number;
  constructor(menuUrlCount: number) {
    super(
      `This theme is used by ${menuUrlCount} menu URL${
        menuUrlCount > 1 ? "s" : ""
      }. Remove the theme from those menu URLs first.`
    );
    this.name = "ThemeInUseError";
    this.menuUrlCount = menuUrlCount;
  }
}
